/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useState } from "react";
import { ISchemaExpressionModel, TGenericModelNode } from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { FieldCode, IFieldCodeProps } from "../../Fields/FieldCode";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { useEditorInspectorContext } from "../../../contexts/EditorInspectorContext";
import { useFloatingPanel } from "../../../hooks/useFloatingPanel";
import { FloatingPanelExpressionEditorModel } from "../../FloatingPanelExpressionEditor/FloatingPanelExpressionEditorModel";

export const ControlExpression_Primary: TInspectorControlComponent<ISchemaExpressionModel> = ({
	model: _model,
	additionalValidationErrors,
	id,
	label,
	path
}) => {
	const inspectorContext = useEditorInspectorContext();

	const { model, validationErrors, runtimeErrors } = useBlueprintModel(_model, "expression");
	const [ expLastValue, setExpLastValue ] = useState<{ result: unknown }>(
		model?.expLastValue as { result: unknown }
	);

	const language: IFieldCodeProps["language"] = "adapptioExpression";

	const { togglePanel, opened } = useFloatingPanel(
		FloatingPanelExpressionEditorModel,
		`panel-${model?.nodeId}`,
		{
			model: _model,
			path,
			readOnly: inspectorContext?.readOnly,
			debounceTimeout: inspectorContext?.inputDebounceTimeout,
			title: label,
			language,
			defaultRect: { width: 500, height: 400 }
		}
	);

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	useEffect(() => {
		if (model) {
			const handleLastValueChange = () => setExpLastValue(model.expLastValue as { result: unknown });

			onEvent(model.expLastValueChangeEvent, handleLastValueChange);

			return () => {
				offEvent(model.expLastValueChangeEvent, handleLastValueChange);
			};
		}
	}, [ model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<FieldCode
			id={id}
			label={label}
			value={model.value}
			onChange={handleChange}
			validationErrors={validationErrors.concat(runtimeErrors).concat(additionalValidationErrors || [])}
			parseErrors={model.expParseErrors}
			resultData={expLastValue?.result}
			grow={true}
			language={language}
			readOnly={inspectorContext?.readOnly}
			debounceTimeout={inspectorContext?.inputDebounceTimeout}
			canExpand
			onExpand={togglePanel}
			isExpanded={opened}
		/>
	);
};

export const ControlConstExpression = (model: TGenericModelNode): IInspectorControls => ({
	primary: {
		component: ControlExpression_Primary,
		model: model
	}
});
